import React, { ReactNode } from 'react';
import { PaymentMethod } from '../../../../../constants/merchantPriceCatalog';
import { FeeField, ProductRow } from '../../../../../services/helpers/merchantPrices';
import { MethodCodes, MethodName } from './styles';

/** The fees only some methods have: recurring purchases. */
export const recurringFields: FeeField[] = ['recurringServiceFee', 'recurringTransactionFee'];

/** Whether the method has the given fee at all; a method without recurring purchases shows a dash there. */
export const hasFee = (method: PaymentMethod, field: FeeField) =>
    method.supportsRecurring || !recurringFields.includes(field);

/** The codes of the method grouped per product type with their index, for a line per type. */
const groupByProductType = (rows: ProductRow[]): [string, [ProductRow, number][]][] => {
    const groups: [string, [ProductRow, number][]][] = [];

    rows.forEach((row, index) => {
        const group = groups.find(([type]) => type === row.productType);

        if (group) group[1].push([row, index]);
        else groups.push([row.productType, [[row, index]]]);
    });

    return groups;
};

interface PaymentMethodCodesProps {
    method: PaymentMethod;
    rows: ProductRow[];
    /** A stable React key per code, e.g. the id of its field array item. */
    rowKey: (row: ProductRow, index: number) => string;
    /** What follows a code, e.g. its remove button. */
    renderAfterCode?: (row: ProductRow, index: number) => ReactNode;
    /** The validity of a campaign code, shown on its own line. */
    renderValidity: (row: ProductRow, index: number) => ReactNode;
}

/**
 * The name of a payment method and its product codes, active or not: a line per code type, or per code for a campaign.
 * The codes are what makes the method possible to price, so they show before it is switched on.
 */
const PaymentMethodCodes: React.FC<PaymentMethodCodesProps> = ({
    method,
    rows,
    rowKey,
    renderAfterCode,
    renderValidity,
}: PaymentMethodCodesProps) => (
    <>
        <MethodName>{method.label}</MethodName>
        {method.hasValidityDates
            ? // Each code of a campaign has its own validity, so it gets a line with its dates.
              rows.map((row, index) => (
                  <MethodCodes key={rowKey(row, index)}>
                      {row.productCode}
                      {renderAfterCode?.(row, index)} <span>· {row.productType}</span>
                      {renderValidity(row, index)}
                  </MethodCodes>
              ))
            : groupByProductType(rows).map(([productType, group]) => (
                  <MethodCodes key={productType}>
                      {group.map(([row, index], position) => (
                          <React.Fragment key={rowKey(row, index)}>
                              {position > 0 && ', '}
                              {row.productCode}
                              {renderAfterCode?.(row, index)}
                          </React.Fragment>
                      ))}{' '}
                      <span>· {productType}</span>
                  </MethodCodes>
              ))}
    </>
);

export default PaymentMethodCodes;
